import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import MInputField from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { getMemberInfo } from "@/hooks/useMembers";
import { isValidName } from "@/utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

const AddMemberName: React.FC = () => {
  const navigate = useNavigate();
  const memberInfo = getMemberInfo(true);
  const [saving, setSaving] = useState(false);

  const saveToLocalStorage = (data: any) => {
    const saved = localStorage.getItem("add_member_form");
    const form = saved ? JSON.parse(saved) : {};
    localStorage.setItem(
      "add_member_form",
      JSON.stringify({ ...form, ...data })
    );
  };

  const [firstName, setFirstName] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).firstName || "" : "";
  });
  const [middleName, setMiddleName] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).middleName || "" : "";
  });
  const [lastName, setLastName] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).lastName || "" : "";
  });
  const [nickname, setNickname] = useState(() => {
    const saved = localStorage.getItem("add_member_form");
    return saved ? JSON.parse(saved).nickname || "" : "";
  });
  const firstNameValid = isValidName(firstName.trim());
  const middleNameValid = !middleName || isValidName(middleName.trim());
  const lastNameValid = isValidName(lastName.trim());
  const allFilled =
    firstName && lastName && firstNameValid && lastNameValid && middleNameValid;

  // Track GTM user_data_loaded event on page load
  React.useEffect(() => {
    if (!memberInfo?.id) return;

    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.FAMILY,
      screenName: ANALYTICS_SCREEN_NAMES.FAMILY_ADD_MEMBER_NAME,
    });
  }, [memberInfo?.id]);

  const handleNext = () => {
    // Save all name fields to add_member_form
    const saved = localStorage.getItem("add_member_form");
    const form = saved ? JSON.parse(saved) : {};
    localStorage.setItem(
      "add_member_form",
      JSON.stringify({
        ...form,
        firstName: firstName.trim(),
        middleName,
        lastName: lastName.trim(),
        nickname,
      })
    );
    navigate("/profile/family/add-member/form/details");
  };

  return (
    <div
      className="profile-page-root flex flex-col"
      style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
    >
      <FormHeader
        module="Family"
        memberInfo={memberInfo}
        title="Set up their profile"
        description="Add details to create their profile."
        learnMoreContent={
          <>
            We match documents using the first and last name you add here. Use
            the name that appears in their medical records.
            <br />
            <br />
            To personalise your experience, you can also add a nickname. If you
            call them Pops or Chintu, we’ll use that when speaking to you. This
            nickname is visible only to you.
          </>
        }
        drawerTitle="Learn more"
      />
      <MSpacer mt={34} />
      <div className="flex flex-col gap-[32px] flex-1">
        <MInputField
          label="What’s their first name?"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={firstName}
          onChange={(e) => {
            const val = e.target.value.trimStart();
            setFirstName(val);
            saveToLocalStorage({ firstName: val.trim() });
          }}
          maxLength={20}
        />
        <MInputField
          label="What’s their last name?"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={lastName}
          onChange={(e) => {
            const val = e.target.value.trimStart();
            setLastName(val);
            saveToLocalStorage({ lastName: val.trim() });
          }}
          maxLength={20}
        />
        <MInputField
          label="Tell us their nickname? (Optional)"
          showLabel
          placeholder="Not Answered"
          variant="Underlined"
          size="md"
          value={nickname}
          onChange={(e) => {
            setNickname(e.target.value);
            saveToLocalStorage({ nickname: e.target.value });
          }}
          onFocus={(e) => {
            const el = e.currentTarget;
            window.setTimeout(() => {
              el?.scrollIntoView({ behavior: "smooth", block: "end" });
            }, 300);
          }}
        />
        <div className="confirm-footer">
          <MButton
            variant="filled"
            size="lg"
            className="w-full"
            disabled={
              !firstName.trim() ||
              !lastName.trim() ||
              !firstNameValid ||
              !lastNameValid ||
              saving
            }
            onClick={handleNext}
          >
            Next
          </MButton>
        </div>
      </div>
    </div>
  );
};

export default AddMemberName;
